import { parseDateTime } from "@internationalized/date";

import i18n from "@/i18n";

import { ettsBootstrap } from "./bootstrap";
import { normalizeLocale } from "./locale";

/** Formátování bajtů na MB/GB. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return "—";
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(0)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} kB`;
  return `${bytes} B`;
}

/**
 * Časy z API jsou naivní `LocalDateTime` — bez offsetu, takže samotný řetězec neříká,
 * v jaké zóně vznikl. `new Date("2026-07-29T12:11:55")` ho přečte jako **místní** čas
 * prohlížeče, jenže server ho zapsal ve své zóně (v Dockeru UTC) → čas by se zobrazoval
 * posunutý o offset uživatele.
 *
 * Zónu serveru proto deklaruje bootstrap (`window.__ETTS__.serverTimeZone`, viz
 * `SpaController`) a přepočet dělá tenhle modul. Časy renderuj **výhradně** přes zdejší
 * funkce, nikdy ad-hoc přes `new Date(x).toLocaleString()`.
 */

/** Řetězec už nese zónu (`…Z` nebo `…+02:00`) → přepočet netřeba. */
const HAS_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/** Jen datum bez času (např. platnost licence) — zóna se na něj nevztahuje. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Jazyk aplikace → BCP-47 tag pro `Intl`. Formát data se řídí **jazykem rozhraní**,
 * ne nastavením prohlížeče — kdo si aplikaci přepne do češtiny, chce české datum
 * i na anglických Windows.
 *
 * `en` je záměrně `en-GB`: rozhraní běží v ČR/SR, kde je `29/07/2026 14:56` čitelnější
 * než americké `07/29/2026 02:56 PM` (nejednoznačné pořadí dne a měsíce, 12hodinový čas).
 */
const LOCALE_TAGS = { cs: "cs-CZ", sk: "sk-SK", en: "en-GB" } as const;

function serverTimeZone(): string {
  return ettsBootstrap().serverTimeZone || "UTC";
}

function uiLocale(): string {
  return LOCALE_TAGS[normalizeLocale(i18n.language) ?? "cs"];
}

/**
 * Naivní čas ze serveru → `Date` (tedy skutečný okamžik). Vrací `null`, když se hodnotu
 * nepodaří přečíst — volající pak zobrazí vstup beze změny, ať se nikdy neztratí informace.
 */
function parseServerDateTime(raw: string): Date | null {
  // Komunikační logy posílají `yyyy-MM-dd HH:mm:ss.SSS` (mezera místo `T`).
  const s = raw.trim().replace(" ", "T");

  if (HAS_ZONE.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  try {
    return parseDateTime(s).toDate(serverTimeZone());
  } catch {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
}

/**
 * Pojistka proti tomu, aby jedno špatně serializované pole shodilo celou stránku.
 *
 * `LocalDateTime` bez `@JsonFormat` odchází ze serveru jako **pole čísel**
 * (`[2026,6,17,11,34,19]`), na kterém `.trim()` vyhodí `e.trim is not a function` —
 * a protože se formátuje při renderu, spadne celý pohled, ne jen jedna buňka.
 * Přesně to dělal detail TLS certifikátu (opraveno na serveru; viz
 * `.claude/rules/datetime-format.md`). Držíme se pravidla „raději vstup beze změny
 * než ztracená informace".
 */
function asDateString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

/** Čas z ISO na HH:mm (pro osy grafů a kroky testovacích scénářů). */
export function formatTime(iso: string): string {
  const raw = asDateString(iso);
  if (raw === null) return String(iso);
  const d = parseServerDateTime(raw);
  if (!d) return raw;
  return d.toLocaleTimeString(uiLocale(), { hour: "2-digit", minute: "2-digit" });
}

/**
 * Formátování času ze serveru na čitelný cs/sk/en formát v zóně prohlížeče.
 *
 * @param opts.seconds sekundy navíc — u logů a auditu, kde se koreluje s auth serverem
 */
export function formatDateTime(
  iso: string | null | undefined,
  opts?: { seconds?: boolean },
): string {
  if (!iso) return "—";

  const raw = asDateString(iso);
  if (raw === null) return String(iso);

  // Hodnota bez času (platnost licence, platnost certifikátu) je kalendářní datum,
  // ne okamžik — převodem mezi zónami by se mohla posunout o den.
  if (DATE_ONLY.test(raw.trim())) {
    const [y, m, day] = raw.trim().split("-").map(Number);
    return new Date(y, m - 1, day).toLocaleDateString(uiLocale(), {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  }

  const d = parseServerDateTime(raw);
  if (!d) return raw;

  return d.toLocaleString(uiLocale(), {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    ...(opts?.seconds ? { second: "2-digit" } : {}),
  });
}

/**
 * Číslo s oddělovači tisíců podle jazyka rozhraní. Hromadné akce běží nad tisíci tokeny
 * a „1 696" se čte lépe než „1696".
 */
export function formatNumber(value: number): string {
  return value.toLocaleString(uiLocale());
}

/**
 * Čas položky aplikačního logu (epoch millis) na `YYYY-MM-DD HH:mm:ss.SSS` v zóně
 * prohlížeče. Pevný formát nezávislý na locale — řádky logu jsou v monospace, mají být
 * zarovnané a seřaditelné.
 */
export function formatLogInstant(millis: number): string {
  const d = new Date(millis);
  const p = (n: number, width = 2) => String(n).padStart(width, "0");
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
    `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}`
  );
}
